/*
  The brand palette, and the source of truth for it.

  Web reads these through CSS custom properties and Tailwind class names; React Native has no such
  thing and needs the literal values. Rather than keep two hand-maintained copies in sync, the values
  live here and `bun run tokens` writes the `:root` block in apps/web/app/globals.css from them. That
  block is generated — edit this file instead, then regenerate.

  Keys are the CSS custom property name without the leading dashes, so `colors['coral-300']` is
  exactly `var(--coral-300)`. No name mangling in either direction.
*/

/** Grouped for the generated stylesheet's section comments, and for humans reading the palette. */
export const colorGroups = {
  Base: {
    cream: '#fcf0ed',
    'blue-link': '#1173fd',
    'stars-bg': '#fed23f', // this color is repeated in --yellow-default
  },
  'Black and White': {
    white: '#fafafa',
    'gray-100': '#eeeeee',
    'gray-200': '#d5d5d5',
    'gray-300': '#bdbdbd',
    'gray-400': '#8b8b8b',
    'gray-500': '#4c4c4c',
    black: '#2f2525',
  },
  Coral: {
    'coral-100': '#ffd7d3', // old --coral-tint-2
    'coral-200': '#ffa297', // new
    'coral-300': '#ff7868', // old --coral-tint-1
    'coral-default': '#ff5743', // old --coral
    'coral-600': '#f0523f', // old --coral-shade-1
    'coral-700': '#e14d3b', // old --coral-shade-2
    'coral-800': '#5b1408', // new
  },
  Orange: {
    'orange-100': '#ffdbbd',
    'orange-200': '#ffbb83',
    'orange-300': '#ff9c4b',
    'orange-default': '#ff841f',
    'orange-600': '#f07c1d',
    'orange-700': '#bd4800',
    'orange-800': '#724204',
  },
  Yellow: {
    'yellow-100': '#fdf4d3', // old --yellow-tint-2
    'yellow-200': '#fee68e', // new
    'yellow-300': '#f8ea54', // old --yellow-tint-1
    'yellow-400': '#ffbb4d',
    'yellow-default': '#fed23f', // old --yellow
    'yellow-600': '#ff792a', // old --orange-shade-1
    'yellow-700': '#af4f06', // old --orange-shade-2
    'yellow-800': '#724204', // new
  },
  Green: {
    'green-100': '#cef3e3',
    'green-200': '#a1ecc7',
    'green-300': '#63de9b',
    'green-400': '#36cea2',
    'green-default': '#00ab7e',
    'green-600': '#00805b',
    'green-700': '#005748',
    'green-800': '#03301e',
  },
  Blue: {
    'blue-100': '#dffdff',
    'blue-200': '#aff8fe',
    'blue-300': '#82e2f3',
    'blue-400': '#9fe7ff',
    'blue-default': '#5daffb',
    'blue-600': '#4996da',
    'blue-700': '#2d6486',
    'blue-800': '#1a3c4f',
  },
  Purple: {
    'purple-100': '#eee5ff',
    'purple-200': '#dbc8fe',
    'purple-300': '#b9a6fc',
    'purple-400': '#aa95f1',
    'purple-default': '#7b67f6',
    'purple-600': '#6d62d0',
    'purple-700': '#484a9d',
    'purple-800': '#373068',
  },
} as const;

/** Every token, flattened — the shape most callers want. */
export const colors = {
  cream: '#fcf0ed',
  'blue-link': '#1173fd',
  'stars-bg': '#fed23f',
  white: '#fafafa',
  'gray-100': '#eeeeee',
  'gray-200': '#d5d5d5',
  'gray-300': '#bdbdbd',
  'gray-400': '#8b8b8b',
  'gray-500': '#4c4c4c',
  black: '#2f2525',
  'coral-100': '#ffd7d3',
  'coral-200': '#ffa297',
  'coral-300': '#ff7868',
  'coral-default': '#ff5743',
  'coral-600': '#f0523f',
  'coral-700': '#e14d3b',
  'coral-800': '#5b1408',
  'orange-100': '#ffdbbd',
  'orange-200': '#ffbb83',
  'orange-300': '#ff9c4b',
  'orange-default': '#ff841f',
  'orange-600': '#f07c1d',
  'orange-700': '#bd4800',
  'orange-800': '#724204',
  'yellow-100': '#fdf4d3',
  'yellow-200': '#fee68e',
  'yellow-300': '#f8ea54',
  'yellow-400': '#ffbb4d',
  'yellow-default': '#fed23f',
  'yellow-600': '#ff792a',
  'yellow-700': '#af4f06',
  'yellow-800': '#724204',
  'green-100': '#cef3e3',
  'green-200': '#a1ecc7',
  'green-300': '#63de9b',
  'green-400': '#36cea2',
  'green-default': '#00ab7e',
  'green-600': '#00805b',
  'green-700': '#005748',
  'green-800': '#03301e',
  'blue-100': '#dffdff',
  'blue-200': '#aff8fe',
  'blue-300': '#82e2f3',
  'blue-400': '#9fe7ff',
  'blue-default': '#5daffb',
  'blue-600': '#4996da',
  'blue-700': '#2d6486',
  'blue-800': '#1a3c4f',
  'purple-100': '#eee5ff',
  'purple-200': '#dbc8fe',
  'purple-300': '#b9a6fc',
  'purple-400': '#aa95f1',
  'purple-default': '#7b67f6',
  'purple-600': '#6d62d0',
  'purple-700': '#484a9d',
  'purple-800': '#373068',
} as const;

export type ColorToken = keyof typeof colors;

/** `var(--x)` for the web, where deferring to the live custom property keeps runtime theming intact. */
export const cssVar = (token: ColorToken): string => `var(--${token})`;
